/**
 * <side-drawer>
 * ---------------------------------------------------------------------------
 * Panel lateral reutilizable: menú móvil y cesta.
 *
 * Se abre desde cualquier elemento con `data-drawer-open="<id>"`.
 *
 * Accesibilidad (no es opcional, es lo que hace usable el panel con teclado
 * y con lector de pantalla):
 *   - El foco entra en el panel al abrirlo y vuelve al disparador al cerrarlo
 *   - Tab queda atrapado dentro mientras está abierto
 *   - Escape cierra
 *   - El fondo de la página se bloquea para que no haga scroll por detrás
 */

// Envuelto en IIFE: varias secciones cargan este archivo y dos <script>
// con la misma URL se ejecutan dos veces. Sin este envoltorio, los `const`
// del ámbito global chocan y el script entero deja de funcionar.
(() => {


const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

class SideDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('[data-drawer-panel]');
    this.name = this.dataset.drawer;

    this.querySelectorAll('[data-drawer-close]').forEach((button) =>
      button.addEventListener('click', () => this.close())
    );

    this.addEventListener('click', (event) => {
      if (event.target === this) this.close();
    });

    document.addEventListener('keydown', this.#onKeydown);

    // Un único delegado global: cualquier disparador funciona, incluso si se
    // añade al DOM después (por ejemplo dentro de una sección recargada).
    if (!document.body.dataset.drawerDelegate) {
      document.body.dataset.drawerDelegate = 'true';
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-drawer-open]');
        if (!trigger) return;
        const target = document.querySelector(
          `side-drawer[data-drawer="${trigger.dataset.drawerOpen}"]`
        );
        if (!target) return; // sin panel en la página, el enlace navega normal
        event.preventDefault();
        target.open(trigger);
      });
    }
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.#onKeydown);
  }

  open(trigger) {
    this.trigger = trigger ?? null;
    this.hidden = false;
    document.documentElement.style.overflow = 'hidden';

    // Un fotograma de margen para que la transición de entrada se vea
    requestAnimationFrame(() => this.classList.add('is-open'));

    this.trigger?.setAttribute('aria-expanded', 'true');
    (this.panel?.querySelector(FOCUSABLE) ?? this.panel)?.focus();

    this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true, detail: { name: this.name } }));
  }

  close() {
    if (this.hidden) return;

    this.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    this.trigger?.setAttribute('aria-expanded', 'false');

    const finish = () => {
      this.hidden = true;
      this.trigger?.focus();
    };

    // Espera a que acabe la animación, salvo que el usuario pida menos movimiento
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finish();
    } else {
      this.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, 400); // red de seguridad si transitionend no llega
    }

    this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true, detail: { name: this.name } }));
  }

  #onKeydown = (event) => {
    if (this.hidden) return;

    if (event.key === 'Escape') {
      this.close();
      return;
    }

    if (event.key !== 'Tab') return;

    // Atrapa el foco dentro del panel
    const focusables = Array.from(this.panel?.querySelectorAll(FOCUSABLE) ?? []).filter(
      (element) => element.offsetParent !== null
    );
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
}

if (!customElements.get('side-drawer')) {
  customElements.define('side-drawer', SideDrawer);
}
})();
